'use client'

import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { SITE_CONFIG } from '@/lib/constants'
import AnimatedLogo from '@/components/effects/AnimatedLogo'

export default function Footer() {
  const t = useTranslations('footer')

  const handleOpenCookieSettings = () => {
    window.dispatchEvent(new CustomEvent('openCookieSettings'))
  }

  return (
    <footer
      id="site-footer"
      // Fixed flagship palette (not the light/dark tokens) so it matches the homepage on every page;
      // translucent glass so the space backdrop shows through.
      className="relative border-t border-white/10 bg-[color-mix(in_srgb,var(--flagship-bg)_55%,transparent)] px-4 py-8 md:px-6 md:py-10 lg:px-12"
      style={{ boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.08)' }}
      role="contentinfo"
      aria-label={t('ariaLabel')}
    >
      <div className="container mx-auto">
        <div className="mx-auto mb-6 grid max-w-4xl grid-cols-2 gap-x-4 gap-y-6 md:mb-8 md:grid-cols-3 md:gap-0 md:divide-x md:divide-white/10">
          {/* Logo & Description */}
          <div className="col-span-2 flex flex-col items-center justify-center text-center md:col-span-1 md:px-6">
            <Link prefetch={false} href="/" className="mb-3 inline-block md:mb-4" aria-label="M2B Solutions">
              <AnimatedLogo size={64} />
            </Link>
            <p
              className="mx-auto max-w-xs text-sm leading-relaxed md:max-w-md"
              style={{ color: 'var(--flagship-muted)' }}
            >
              {t('description')}
            </p>
          </div>

          {/* Contact */}
          <nav aria-label={t('contactHeading')} className="footer-contact flex flex-col items-center justify-start text-center md:justify-center md:px-6">
            <h3 className="mb-1 font-semibold md:mb-4 [font-family:var(--font-ui)]" style={{ color: 'var(--flagship-text)' }}>
              {t('contactHeading')}
            </h3>
            <ul className="md:space-y-2">
              <li>
                <a
                  href={`mailto:${SITE_CONFIG.email}`}
                  className="flex items-center justify-center text-sm transition-opacity hover:opacity-70"
                  style={{ color: 'var(--flagship-muted)' }}
                >
                  {SITE_CONFIG.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${SITE_CONFIG.phone}`}
                  className="flex items-center justify-center text-sm transition-opacity hover:opacity-70"
                  style={{ color: 'var(--flagship-muted)' }}
                >
                  {SITE_CONFIG.phone}
                </a>
              </li>
              <li>
                <Link prefetch={false}
                  href="/website-check"
                  className="flex items-center justify-center text-sm transition-opacity hover:opacity-70"
                  style={{ color: 'var(--flagship-muted)' }}
                >
                  {t('websiteCheckLabel')}
                </Link>
              </li>
            </ul>
          </nav>

          {/* Legal */}
          <nav aria-label={t('legalHeading')} className="footer-legal flex flex-col items-center justify-start text-center md:justify-center md:px-6">
            <h3 className="mb-1 font-semibold md:mb-4 [font-family:var(--font-ui)]" style={{ color: 'var(--flagship-text)' }}>
              {t('legalHeading')}
            </h3>
            <ul className="md:space-y-2">
              <li>
                <Link prefetch={false}
                  href="/imprint"
                  className="flex items-center justify-center text-sm transition-opacity hover:opacity-70"
                  style={{ color: 'var(--flagship-muted)' }}
                >
                  {t('imprintLabel')}
                </Link>
              </li>
              <li>
                <Link prefetch={false}
                  href="/privacy"
                  className="flex items-center justify-center text-sm transition-opacity hover:opacity-70"
                  style={{ color: 'var(--flagship-muted)' }}
                >
                  {t('privacyLabel')}
                </Link>
              </li>
              <li>
                <button
                  onClick={handleOpenCookieSettings}
                  className="flex w-full items-center justify-center text-center text-sm transition-opacity hover:opacity-70"
                  style={{ color: 'var(--flagship-muted)' }}
                  type="button"
                >
                  {t('cookieSettingsLabel')}
                </button>
              </li>
            </ul>
          </nav>
        </div>

        <p className="mx-auto max-w-none pb-2 text-center text-xs md:pb-4" style={{ color: 'var(--flagship-muted)' }}>
          <a
            href="https://sketchfab.com/3d-models/wonderful-world-3b377d37ddfe4e818570d013a0710c15"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block underline underline-offset-2"
          >
            {t('modelCredit')}
          </a>
        </p>

        {/* Bottom Bar */}
        <div
          className="flex flex-col items-center justify-center gap-x-6 gap-y-1 pt-4 text-center text-xs md:flex-row md:pt-6 md:text-sm"
          style={{ borderTop: '1px solid rgba(255,255,255,0.1)', color: 'var(--flagship-muted)' }}
        >
          <p>{t('copyright')}</p>
          <p>{t('madeWith')}</p>
        </div>
      </div>
    </footer>
  )
}
